/**
 * NOTE:
 * Do not put code that executes as an IIFE in this file. Any time you need to run
 * initialization on a Marketo Form, you should import initMarketoForm() and call
 * it directly from the parent script.
 *
 */

/* global ajax_params */ // added through wp_localize_script
import { cookie, getPDCookie } from "./lib/cookies";
import { isIE } from "./lib/ie-helpers";
import { getUTMSFromURL } from "./lib/url-helpers";
import { isPdEmployee } from "./lib/pd-helpers.js";
import { mkwid, gclid } from "./lib/sign-up.js";
import {
  trackMarketoFormSubmit,
  formElementUserInputDetected,
  marketoFormEngagementDetected,
  trackCloudflareEnrichment,
} from "./segment-tracks";
import { trackGenericEvent } from "./segment/segment-calls";
import { getCloudflareData, countryLookupTable } from "./lib/cf-helpers.js";

/**
 * @param {string} key
 */
function getParamByKey(key) {
  key = key.replace(/[[]/, "\\[").replace(/[\]]/, "\\]");
  const regex = new RegExp("[\\?&]" + key + "=([^&#]*)"),
    results = regex.exec(location.search);
  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
}

const mkwidParam = getParamByKey("mkwid");
const gclidParam = getParamByKey("gclid");

const marketingOptIn = `
<div class="mb-gut/2">
  <div>
    I would like to receive marketing communications from PagerDuty Marketing. I understand that my personal information will be handled as described in the <a class="text-green font-bold hover:opacity-75" target="_blank" rel="noopener" href="/privacy-policy/">PagerDuty Privacy Policy</a>, and that I can opt out of marketing communications at any time by managing my <a class="text-green font-bold hover:opacity-75" target="_blank" rel="noopener" href="https://info.pagerduty.com/2024-Preference-Center.html">marketing email preferences</a>.
  </div>
  <input name="internationalMarketingOptIn" id="marketing-opt-in" type="radio" value="1" required>
  <label for="marketing-opt-in" style="position:static; transform:none; padding:0; margin:0 1rem 0 0">Yes</label>
  <input name="internationalMarketingOptIn" id="marketing-opt-out" type="radio" value="0" required>
  <label for="marketing-opt-out" style="position:static; transform:none; padding:0; margin:0">No</label>
</div>`;
const countryField = document.getElementById("Form_Country__c");
const stateField = document.getElementById("Form_State__c");
const partnerTypeField = document.getElementById("Partner_Type__c");
const recurringWebinarDate = document.getElementById("recurringWebinarDate");
const numOfEmployees = document.getElementById("NumberOfEmployees");
const currentlyInTrial = document.getElementById(
  "Demo_Form_Currently_in_Trial__c",
);
let engagmentTrackFired = false;

// Get all form mktoFormInputs and setup EventListeners
const mktoFormInputs = document.querySelectorAll(
  '#mkto-api-form input[type="text"], #mkto-api-form input[type="email"], #mkto-api-form select',
);
const form = document.getElementById("mkto-api-form");

const userInputTrackerIds = [
  "email-nodemandbase", // email
  "FirstName", // First-name
  "LastName", // Last-name
  "Company", // Last-name
  "Title",
  "Form_Country__c",
  "Form_State__c",
];

/** Replacement character for `"` */
const fullWidthQuotationMark = "\uFF02"; // FULLWIDTH QUOTATION MARK: ＂
const rxQuote = new RegExp('"', "gm");

/**
 * @param {NodeListOf<Element>} elements
 */
function setupFormEngagmentInputEventListeners(elements) {
  for (let i = 0; i < elements.length; i++) {
    if (elements[i]) {
      if (elements[i] instanceof HTMLInputElement)
        elements[i]?.addEventListener("keyup", sendEngagementTrack, {
          once: true,
        });
      // Removing this because Form Engagment track was getting fired when the value of this select element was getting
      // auto populated from CF data, which we don't want happening.
      // if (elements[i] instanceof HTMLSelectElement) elements[i]?.addEventListener("change", sendEngagementTrack, { once: true });
    }
  }
}

/**
 * @param {{ target: any; }} evt
 */
function sendEngagementTrack(evt) {
  const target = evt.target;
  if (!engagmentTrackFired) {
    const options = {
      program_name: /** @type {HTMLInputElement} */ (
        document.querySelector("#mkto-api-form input[name='programName']")
      )?.value,
      field_name: target?.getAttribute("name"),
    };
    marketoFormEngagementDetected(options);
  }
  engagmentTrackFired = true;
}

function setupInputListeners() {
  for (const id of userInputTrackerIds) {
    const elm = document.getElementById(id);
    if (elm) {
      if (elm instanceof HTMLInputElement)
        elm?.addEventListener("keyup", onInputKeyup, { once: true });
      if (elm instanceof HTMLSelectElement)
        elm?.addEventListener("change", onInputKeyup, { once: true });
    }
  }
}

/**
 * @param {any} evt
 */
function onInputKeyup(evt) {
  if (
    evt &&
    evt.target instanceof HTMLElement &&
    userInputTrackerIds.includes(evt.target.id)
  ) {
    sendUserInputTrack(evt.target.id);
  }
}

/**
 * @param {string} id
 */
function sendUserInputTrack(id) {
  const tracks = {};
  tracks.form_type = "Marketo Form";
  tracks.field_name = id.toLowerCase();
  tracks.is_pd_employee = isPdEmployee([
    "FirstName",
    "LastName",
    "email-nodemandbase",
    "Company",
  ]);
  const event = "Marketo Form Input Detected";
  formElementUserInputDetected(event, tracks);
}

if (isIE()) {
  //Fix label placement above TextFields
  const labels = document.querySelectorAll(".mkto-validation label");
  if (window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = function (
      /** @type {{ call: (arg0: any, arg1: Node | undefined, arg2: number, arg3: NodeList) => void; }} */ callback,
      /** @type {Window & typeof globalThis} */ thisArg,
    ) {
      thisArg = thisArg || window;
      for (var i = 0; i < this.length; i++) {
        callback.call(thisArg, this[i], i, this);
      }
    };
  }
  labels.forEach(function (el) {
    if (typeof el.id != "undefined" && el.id == "label-comments") {
      el.setAttribute("style", "transform:translate(0px, -25px)");
    } else {
      el.setAttribute("style", "transform:translate(0px, -90px)");
    }
  });
}

/** @arg {Event} e */
function moveLabelUp(e) {
  const target = /** @type {HTMLElement | null} */ (e.target);
  if (target) {
    const label = target.nextElementSibling;
    if (label) {
      label.classList.add("active");
      label.classList.remove("blurred");
    }
  }
}

/** @arg {Event} e */
function selectBlurred(e) {
  const target = /** @type {HTMLElement | null} */ (e.target);
  if (target) {
    const label = target.nextElementSibling;
    if (label) label.classList.add("blurred");
  }
}

/** @arg {Event} e */
function changeCountry(e) {
  const target = /** @type {HTMLInputElement | null} */ (e.target);
  if (target) {
    updateUI(target.value);
  }
}

/** @arg {Event} e */
function trackChangeCountry(e) {
  const target = /** @type {HTMLInputElement | null} */ (e.target);
  if (target)
    trackGenericEvent(
      "select-value-changed",
      ["mkto-form-country-field"],
      countryField?.value,
    );
}

/**
 * @param {string} selectValue
 * @param {string} [region]
 */
function updateUI(selectValue, region) {
  const stateHolder = document.getElementById("state-holder");
  const optInPlacholder = document.getElementById(
    "intl-marketing-opt-in-placeholder",
  );
  const att = document.createAttribute("required");
  if (stateField && stateHolder) {
    if (selectValue === "United States") {
      if (optInPlacholder != null) {
        optInPlacholder.classList.add("hidden");
        optInPlacholder.innerHTML = "";
        optInPlacholder.removeAttribute("required");
      }
      stateHolder.classList.remove("hidden");
      stateField.setAttributeNode(att);
      if (region) stateField.value = region;
    } else {
      if (optInPlacholder != null) {
        optInPlacholder.classList.remove("hidden");
        optInPlacholder.innerHTML = marketingOptIn;
        optInPlacholder.setAttributeNode(att);
      }
      stateHolder.classList.add("hidden");
      stateField.removeAttribute("required");
    }
  }
}

/**
 * @param {HTMLSelectElement} selectEl
 */
function addSelectEventListeners(selectEl) {
  if (selectEl) {
    // These listeners are used for animating the labels
    if (!isIE()) {
      selectEl.addEventListener("focus", moveLabelUp);
      selectEl.addEventListener("blur", selectBlurred);
    }
  }
}

/** @arg selectElm {HTMLSelectElement} */
function simulateDropdownInteraction(selectElm) {
  selectElm.dispatchEvent(new Event("focus"));
  selectElm.dispatchEvent(new Event("change"));
}

// TODO(Mark): remove this when they fix the field in Marketo
/**
 * @param {FormData} formData
 */
function fixMktoData(formData) {
  const annualSales = formData.get("DemandBase_Annual_Sales__c");
  if (annualSales && +annualSales > 2147483647) {
    formData.set("DemandBase_Annual_Sales__c", "2147483647");
  }
  return formData;
}

/**
 * NOTE: ea = Early Access
 * @arg {HTMLFormElement} mktoForm
 */
function buildWebComments(mktoForm) {
  const tempCommentsKey = "Web_Comments__temp";
  const commentsKey = "Web_Comments__c";
  const eaFeatureKey = "early-access-feature";

  const elFeatures = mktoForm.querySelectorAll(`[name="${eaFeatureKey}"]`);
  if (!elFeatures || elFeatures.length === 0) return; // There are no Early Access Features so bail early.

  /** @type {string[]} */
  const features = [];
  elFeatures.forEach((it) => {
    const cb = /** @type {HTMLInputElement} */ (it);
    if (cb.checked) features.push(cb.value.trim());
  });

  /** @type {string[]} */
  const comment = [];
  /** @type {HTMLTextAreaElement | null} */
  const ta = mktoForm.querySelector(`[name="${tempCommentsKey}"]`);
  if (ta && 0 < ta.value.trim().length) comment.push(ta.value.trim());

  if (0 < features.length) {
    comment.push(
      "----------------------",
      "# Features of interest",
      features.map((it) => `- ${it}`).join("\n"),
    );
  }

  /** @type {HTMLTextAreaElement | null} */
  const commentsTa = mktoForm.querySelector(`[name="${commentsKey}"]`);
  if (commentsTa) commentsTa.value = comment.join("\n");
}

/**
 * Replaces all `"` with `＂` in all fields that have a `maxlength`.
 * This is necessary because marketo counts escaped double quotes `\"` as two
 * characters.
 * @arg {HTMLFormElement} mktoForm
 */
function replaceDoubleQuotesWithFullWidthQuotes(mktoForm) {
  const nodeList = mktoForm.querySelectorAll("[maxlength]");
  for (let n = nodeList.length; n--; ) {
    if ("value" in nodeList[n]) {
      const node = /** @type {HTMLInputElement} */ (nodeList[n]);
      node.value = node.value.replace(rxQuote, fullWidthQuotationMark);
    }
  }
  return mktoForm;
}

function isEarlyAccess() {
  const checkboxes = document.querySelectorAll(
    "#feature-selection input[type=checkbox]",
  );
  return checkboxes.length > 0;
}
function isValidEarlyAccessSelection() {
  const checkboxes = document.querySelectorAll(
    "#feature-selection input[type=checkbox], #copilot-feature-selection input[type=checkbox]",
  );
  let hasSelection = false;
  for (const cb of checkboxes) {
    if (cb instanceof HTMLInputElement) {
      if (cb.type === "checkbox" && cb.checked) {
        hasSelection = true;
        break;
      }
    }
  }
  const errorMsg = document.getElementById("feature-selection-error");
  if (errorMsg instanceof HTMLElement) {
    errorMsg.classList[hasSelection ? "add" : "remove"]("hidden");
  }
  return hasSelection;
}

/** @arg {Event} e */
export function handleFormSubmission(e) {
  e.preventDefault();

  if (isEarlyAccess()) {
    if (!isValidEarlyAccessSelection()) return;
  }

  const btnApiFormSubmit = /** @type {HTMLInputElement | null} */ (
    document.getElementById("btn-mkto-api-form")
  );
  if (btnApiFormSubmit) {
    btnApiFormSubmit.innerHTML =
      'Please Wait <i class="fa fa-spinner fa-spin"></i>';
    btnApiFormSubmit.disabled = true;

    const mktoForm = /** @type {HTMLFormElement | null} */ (
      document.getElementById("mkto-api-form")
    );
    if (mktoForm == null) return;

    replaceDoubleQuotesWithFullWidthQuotes(mktoForm);

    // Lets see if the page want us to redirect back with a thank-you message.
    const ty = thankYouMessage();
    if (ty) {
      sessionStorage.setItem("tyPath", window.location.pathname);
      setFollowUpPage(mktoForm);
    }

    buildWebComments(mktoForm);

    // send Segment Tracks
    const pn = document.querySelector("[name='programName']");
    /** @type {import('./segment-tracks').MktoFormOptions} */
    const options = {
      program_name: pn instanceof HTMLInputElement ? pn.value || "NA" : "NA",
      is_pd_employee: isPdEmployee([
        "FirstName",
        "LastName",
        "email-nodemandbase",
        "Company",
      ]),
      mkwid: mkwid(),
      gclid: gclid(),
    };
    const firstName = /** @type {HTMLInputElement | null} */ (
      document.getElementById("FirstName")
    );
    const lastName = /** @type {HTMLInputElement | null} */ (
      document.getElementById("LastName")
    );
    const email = /** @type {HTMLInputElement | null} */ (
      document.getElementById("email-nodemandbase")
    );
    if (firstName && lastName)
      options["name"] = firstName.value + " " + lastName.value;
    if (email) options["email"] = email.value;
    trackMarketoFormSubmit(options);

    // form submits
    mktoForm.submit();
  }
}

/**
 *
 * @arg {HTMLFormElement} mktoForm
 */
function setFollowUpPage(mktoForm) {
  const followUpUrlElm = /** @type {HTMLInputElement | null} */ (
    document.getElementById("follow_up_url")
  );
  if (followUpUrlElm) {
    followUpUrlElm.value = window.location.pathname;
  } else {
    const el = document.createElement("input");
    el.setAttribute("type", "hidden");
    el.setAttribute("id", "follow_up_url");
    el.setAttribute("name", "follow_up_url");
    el.setAttribute("value", window.location.pathname);
    mktoForm.appendChild(el);
  }
}

/**
 *
 * @returns {string | boolean}
 */
function thankYouMessage() {
  const thankYouInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById("form_ty_message")
  );
  return thankYouInput?.value ? thankYouInput.value : false;
}

function displayThankYouMessage() {
  const thankYouInput = /** @type {HTMLInputElement | null} */ (
    document.getElementById("form_ty_message")
  );
  const thankYouHTML = /** @type {HTMLInputElement | null} */ (
    document.getElementById("ty-message-html")
  );
  if (thankYouInput && thankYouInput.value) {
    const el = document.getElementById(thankYouInput.value);
    const form = document.getElementById("mkto-api-form");
    document.getElementById("form-heading")?.classList.add("hidden");
    if (el) el.classList.remove("hidden");
    if (form) form.classList.add("hidden");

    const thankYouAnchor = /** @type {HTMLInputElement | null} */ (
      document.getElementById("thank-you-anchor")
    );
    if (thankYouAnchor) window.location.href = "#thank-you-anchor";
  }
  if (thankYouHTML) thankYouHTML.classList.remove("hidden");
}

/**
 * @param {string | number} utm
 * @param {any} val
 */
function updateUniversalFormHiddenField(utm, val) {
  const ftCookie = getPDCookie("ft_" + utm);
  const ltCookie = getPDCookie("lt_" + utm);
  if (!ftCookie) {
    addValToHiddenField("ft_", utm, val);
  } else {
    addValToHiddenField("ft_", utm, ftCookie);
  }
  addValToHiddenField("lt_", utm, ltCookie ? ltCookie : val);
}

/**
 * @param {string} prefix
 * @param {any} utm
 * @param {string | number | string[] | ((this: HTMLElement, index: number, value: string) => string)} val
 */
function addValToHiddenField(prefix, utm, val) {
  const fieldStr = prefix + utm + "__c";
  document.getElementsByName(fieldStr)[0]
    ? (document.getElementsByName(fieldStr)[0].value = val)
    : "";
}

// Requires Cloudflare to be running - If testing locally run task cf:start-wrangler-local
// Will auto populate country and state in the marketo form if they exist.
export function initCloudflareEnrichment() {
  const opts = {
    country: "n/a",
    region: "n/a",
    status: "failed",
    reason: "n/a",
  };
  const cfData = getCloudflareData();
  if (cfData) {
    const country = /** @type {any} */ (cfData.country);
    const region = /** @type {any} */ (cfData.region);
    opts.country = country;
    opts.region = region;

    // @ts-ignore
    if (countryField && countryLookupTable[country]) {
      // @ts-ignore
      countryField.value = countryLookupTable[country];
      updateUI(countryField.value, cfData.region);
      simulateDropdownInteraction(countryField);
      simulateDropdownInteraction(stateField);
    }
    opts.status = "success";
    trackCloudflareEnrichment(opts);

    if (countryField) {
      // These listeners are used for animating the labels
      countryField.addEventListener("change", trackChangeCountry);
    }
  } else {
    opts.reason = "Cloudflare data not available";
    trackCloudflareEnrichment(opts);
  }
}

/**
 * If the 'form-submission-error' cookie is set:
 * Clear the cookie and display the #form-submission-error element.
 */
function conditionallyDisplayWarning() {
  if (cookie("form-submission-error") != null) {
    cookie("form-submission-error", null, { path: "/" });
    const errorElm = document.getElementById("form-submission-error");
    if (errorElm instanceof HTMLElement) {
      errorElm.classList.remove("hidden");
    }
  }
}

export function initMarketoForm() {
  // Some forms include a warning message that's hidden by default but should be
  // shown if the users visits the page a 2nd time after being redirected
  // because their previous submission didn't go through.
  conditionallyDisplayWarning();

  setupFormEngagmentInputEventListeners(mktoFormInputs);
  if (countryField) {
    // These listeners are used for animating the labels
    countryField.addEventListener("change", changeCountry);
  }
  addSelectEventListeners(countryField);
  addSelectEventListeners(stateField);
  addSelectEventListeners(partnerTypeField);
  addSelectEventListeners(recurringWebinarDate);
  addSelectEventListeners(numOfEmployees);
  addSelectEventListeners(currentlyInTrial);

  setupInputListeners();

  window.addEventListener("pageshow", function onPageshow() {
    // If the field has a value (e.g.: clicking back after a submission) then move
    // the label out of the way by simulating a change event
    if (
      countryField instanceof HTMLSelectElement &&
      countryField.selectedIndex !== 0
    ) {
      simulateDropdownInteraction(countryField);
    }

    // If the field has a value (e.g.: clicking back after a submission) then move
    // the label out of the way by simulating a change event
    if (
      stateField instanceof HTMLSelectElement &&
      getComputedStyle(stateField).display !== "none" &&
      stateField.selectedIndex !== 0
    ) {
      simulateDropdownInteraction(stateField);
    }
  });

  // Is the user coming from a submitted form redirect?
  // If the user filled out a form with a TY message, it will
  // set session storage and redirect them back
  const tyPath = sessionStorage.getItem("tyPath");
  if (tyPath) {
    if (tyPath === window.location.pathname) {
      displayThankYouMessage();
      sessionStorage.removeItem("tyPath");
    }
  }

  if (form) {
    form.addEventListener("submit", handleFormSubmission, false);
  }

  if (mkwidParam) {
    document.getElementsByName("Mkwid__c")[0]
      ? (document.getElementsByName("Mkwid__c")[0].value = mkwidParam)
      : "";
  } else if (mkwid()) {
    document.getElementsByName("Mkwid__c")[0]
      ? (document.getElementsByName("Mkwid__c")[0].value = mkwid())
      : "";
  }

  if (gclidParam) {
    document.getElementsByName("gclid__c")[0]
      ? (document.getElementsByName("gclid__c")[0].value = gclidParam)
      : "";
  } else if (gclid()) {
    document.getElementsByName("lt_referral_url__c")[0]
      ? (document.getElementsByName("lt_referral_url__c")[0].value = url)
      : "";
  }

  // Set the value of the Phone and Country fields
  const url = window.location.href;
  const utms = getUTMSFromURL(url);

  for (const utm in utms) {
    updateUniversalFormHiddenField(utm, utms[utm]);
  }
}
